
import { toast } from 'sonner';
import { readContract, writeContract } from '@wagmi/core';
import { config } from '../components/Web3Provider';
import { bsc } from 'wagmi/chains';
import { parseEther, formatEther } from 'viem';

// PancakeSwap Router V2 contract address
const PANCAKESWAP_ROUTER_ADDRESS = '0x10ED43C718714eb63d5aA57B78B54704E256024E';
const TOP1_TOKEN_ADDRESS = '0xe4319b352ca79451c19d37c4728ba981aeeac9fa';
const WBNB_ADDRESS = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c';

export interface LiquidityOptions {
  tokenAmount: string; // Amount of TOP1 tokens
  bnbAmount: string;   // Amount of BNB
  slippage: number;    // Slippage tolerance (e.g., 0.5 for 0.5%)
}

export const calculateLiquidityRequirement = (action: 'reroll' | 'brometheus'): LiquidityOptions => {
  if (action === 'reroll') {
    return {
      tokenAmount: '5000',
      bnbAmount: '0.1', // This will be calculated dynamically
      slippage: 0.5
    };
  } else {
    return {
      tokenAmount: '50000',
      bnbAmount: '1.0', // This will be calculated dynamically
      slippage: 0.5
    };
  }
};

export const estimateLiquidityValue = async (tokenAmount: string): Promise<string> => {
  try {
    console.log('Estimating BNB value for', tokenAmount, 'TOP1 tokens');
    
    const tokenAmountWei = parseEther(tokenAmount);
    const path = [TOP1_TOKEN_ADDRESS as `0x${string}`, WBNB_ADDRESS as `0x${string}`];
    
    const amounts = await readContract(config, {
      address: PANCAKESWAP_ROUTER_ADDRESS as `0x${string}`,
      abi: [{
        "inputs": [
          {"internalType": "uint256", "name": "amountIn", "type": "uint256"},
          {"internalType": "address[]", "name": "path", "type": "address[]"}
        ],
        "name": "getAmountsOut",
        "outputs": [
          {"internalType": "uint256[]", "name": "amounts", "type": "uint256[]"}
        ],
        "stateMutability": "view",
        "type": "function"
      }],
      functionName: 'getAmountsOut',
      args: [tokenAmountWei, path],
      chainId: bsc.id,
    }) as bigint[];
    
    const bnbAmount = formatEther(amounts[1] ?? 0n);
    console.log('Estimated BNB amount:', bnbAmount);
    return bnbAmount;
  } catch (error) {
    console.error('Error estimating liquidity value:', error);
    // Fallback to approximate values based on token amount
    const tokenNum = parseFloat(tokenAmount);
    if (tokenNum >= 50000) return '1.0';
    if (tokenNum >= 5000) return '0.1';
    return '0.05';
  }
};

export const provideLiquidity = async (
  walletAddress: string,
  options: LiquidityOptions,
  action: 'reroll' | 'brometheus'
): Promise<{ success: boolean; txHash?: string; error?: string }> => {
  try {
    console.log(`Providing liquidity for ${action}:`, options);
    
    // Get real-time BNB amount estimate
    const estimatedBnb = await estimateLiquidityValue(options.tokenAmount);
    const bnbAmount = parseEther(estimatedBnb);
    
    // Convert token amount to wei
    const tokenAmount = parseEther(options.tokenAmount);
    
    // Calculate minimum amounts with slippage
    const minTokenAmount = tokenAmount * BigInt(Math.floor((100 - options.slippage) * 100)) / BigInt(10000);
    const minBnbAmount = bnbAmount * BigInt(Math.floor((100 - options.slippage) * 100)) / BigInt(10000);
    
    // Set deadline (20 minutes from now)
    const deadline = BigInt(Math.floor(Date.now() / 1000) + (20 * 60));
    
    console.log('Adding liquidity with params:', {
      token: TOP1_TOKEN_ADDRESS,
      tokenAmount: tokenAmount.toString(),
      minTokenAmount: minTokenAmount.toString(),
      minBnbAmount: minBnbAmount.toString(),
      to: walletAddress,
      deadline: deadline.toString(),
      value: bnbAmount.toString()
    });
    
    // Add liquidity using wagmi
    const hash = await writeContract(config, {
      address: PANCAKESWAP_ROUTER_ADDRESS as `0x${string}`,
      abi: [{
        "inputs": [
          {"internalType": "address", "name": "token", "type": "address"},
          {"internalType": "uint256", "name": "amountTokenDesired", "type": "uint256"},
          {"internalType": "uint256", "name": "amountTokenMin", "type": "uint256"},
          {"internalType": "uint256", "name": "amountETHMin", "type": "uint256"},
          {"internalType": "address", "name": "to", "type": "address"},
          {"internalType": "uint256", "name": "deadline", "type": "uint256"}
        ],
        "name": "addLiquidityETH",
        "outputs": [
          {"internalType": "uint256", "name": "amountToken", "type": "uint256"},
          {"internalType": "uint256", "name": "amountETH", "type": "uint256"},
          {"internalType": "uint256", "name": "liquidity", "type": "uint256"}
        ],
        "stateMutability": "payable",
        "type": "function"
      }],
      functionName: 'addLiquidityETH',
      args: [
        TOP1_TOKEN_ADDRESS as `0x${string}`,
        tokenAmount,
        minTokenAmount,
        minBnbAmount,
        walletAddress as `0x${string}`,
        deadline
      ],
      value: bnbAmount,
      chain: bsc,
      account: walletAddress as `0x${string}`,
    });
    
    console.log('Liquidity transaction submitted:', hash);
    toast.success(`Liquidity provided successfully! ${action} can now proceed.`);
    return { success: true, txHash: hash };
    
  } catch (error: any) {
    console.error('Error providing liquidity:', error);
    
    if (error.code === 'ACTION_REJECTED') {
      return { success: false, error: 'Transaction rejected by user' };
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      return { success: false, error: 'Insufficient funds for liquidity provision' };
    } else {
      return { success: false, error: error.message || 'Failed to provide liquidity' };
    }
  }
};
